import { useEffect, useRef, useState } from 'react';

/**
 * useAnimationCoordinator — Single rAF loop shared by all canvas/particle systems
 * Eliminates 5+ concurrent rAF loops running simultaneously
 *
 * Importers/Callers (planned):
 * - GoingMerryPage.jsx: TensorCanvas, ArchitectureStack, ParticleFlow
 * - ICEPage.jsx: MemoryVisualizer, ICEHeroOrbs
 * - HomePage.jsx: OrbitalHero
 *
 * API:
 * - time: current time in seconds (shared across all subscribers)
 * - subscribe(id, callback): register a callback to receive time updates each frame
 * - unsubscribe(id): remove a subscriber
 *
 * Data schemas: None - pure timing utility
 *
 * User's verbatim instruction:
 * "nah all the animations i mean the code snippets things and the memory visualizer, the marquees scroll animation, benchmark metrics, under the hood, the typewriting animation... like wise all the animations or fucked up, stuck, or slow in animation or totally irrelevant to the product... so we have to make a really unieuq speerate plan for this issue"
 */
export const useAnimationCoordinator = () => {
  const [time, setTime] = useState(0);
  const subscribersRef = useRef(new Map());

  useEffect(() => {
    let frame = 0;
    const tick = (ts) => {
      const t = ts / 1000;
      setTime(t);
      subscribersRef.current.forEach((cb) => cb(t));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  const subscribe = (id, callback) => {
    subscribersRef.current.set(id, callback);
    return () => {
      subscribersRef.current.delete(id);
    };
  };

  const unsubscribe = (id) => {
    subscribersRef.current.delete(id);
  };

  return { time, subscribe, unsubscribe };
};

export default useAnimationCoordinator;